import Voca from 'voca';
import Builder from './builder'
import Client from './client'
export function extractModelName(name) {
    return (Voca.camelCase(name));
}
const suffix = {
    read: '',
    readMany: 'List',
    count: 'Count',
    create: 'Create',
    createMany: 'CreateMany',
    update: 'Update',
    updateMany: 'UpdateMany',
    delete: 'Delete',
    deleteMany: 'DeleteMany',
};
function AccessorQueryBuilder(type, name, model, client) {
    const local = {
        subscription: {
            active: false,
            cached: '',
            callbacks: [],
            models: [model],
        },
    };
    function promise() {
        const builder = Builder(client)
            .withName(name + suffix[type])
            .withArgs({
            filter: local.filter,
            sort: local.sort,
            skip: local.skip,
            limit: local.limit,
        });
        if (type !== 'count') {
            builder
                .withSelection(local.selection || { _id: true });
        }
        return builder
            .asQuery();
    }
    async function subscription(force) {
        const result = await promise();
        const cached = JSON.stringify(result);
        if (force || cached !== local.subscription.cached) {
            local.subscription.callbacks.forEach((callback) => callback(result));
        }
        local.subscription.cached = cached;
    }
    function updateSubscription(unsubscribe) {
        if (unsubscribe || !local.subscription.active) {
            local.subscription.models.forEach((m) => {
                client.unsubscribe(`${m}-updated`, subscription);
            });
        }
        else {
            local.subscription.models.forEach((m) => {
                client.subscribe(`${m}-updated`, subscription);
            });
        }
        if (local.subscription.active) {
            subscription(true);
        }
    }
    const instance = {
        where(filter) {
            local.filter = filter;
            updateSubscription();
            return instance;
        },
        sort(sort) {
            local.sort = sort;
            updateSubscription();
            return instance;
        },
        skip(skip) {
            local.skip = skip;
            updateSubscription();
            return instance;
        },
        limit(limit) {
            local.limit = limit;
            updateSubscription();
            return instance;
        },
        select(selection) {
            local.selection = selection;
            updateSubscription();
            return instance;
        },
        subscribe(callback) {
            if (!local.subscription.callbacks.includes(callback)) {
                local.subscription.callbacks.push(callback);
            }
            local.subscription.active = true;
            updateSubscription();
            return instance;
        },
        unsubscribe(callback) {
            if (local.subscription.callbacks.includes(callback)) {
                local.subscription.callbacks.splice(local.subscription.callbacks.indexOf(callback), 1);
            }
            local.subscription.active = local.subscription.callbacks.length > 0;
            updateSubscription();
            return instance;
        },
        listen(...models) {
            updateSubscription(true);
            local.subscription.models = [...models, model];
            updateSubscription();
            return instance;
        },
        then(callback) {
            return promise().then(callback);
        },
        catch(callback) {
            return promise().catch(callback);
        },
        finally(callback) {
            return promise().finally(callback);
        },
    };
    return instance;
}
function AccessorMutationBuilder(type, name, client) {
    const local = {};
    function promise() {
        if ((type === 'update' || type === 'create') && !local.record) {
            throw new Error(`You need a record to call ${type}. Use withRecord(record)`);
        }
        if (type === 'update' && local.record && !local.record._id) {
            throw new Error('You need a record ID to call update. Use withId(id)');
        }
        if ((type === 'updateMany' || type === 'createMany') && !local.records) {
            throw new Error(`You need records to call ${type}. Use withRecords(records)`);
        }
        if (type === 'updateMany' && local.records && !!local.records.find((r) => !r._id)) {
            throw new Error('Records need to have IDs to call updateMany.');
        }
        if (type === 'delete' && !local.id) {
            throw new Error('You need an id to call delete. Use withId(id)');
        }
        if (type === 'deleteMany' && !local.ids) {
            throw new Error('You need ids to call deleteMany. Use withIds(ids)');
        }
        return Builder(client)
            .withName(name + suffix[type])
            .withSelection(local.selection || { _id: true })
            .withArgs({
            _id: local.id,
            _ids: local.ids,
            record: local.record,
            records: local.records,
        })
            .asMutation();
    }
    const instance = {
        withId(id) {
            if (type === 'update') {
                if (id) {
                    local.record = {
                        ...(local.record || {}),
                        _id: id,
                    };
                }
            }
            else {
                local.id = id;
            }
            return instance;
        },
        withIds(...ids) {
            if (Array.isArray(ids[0])) {
                local.ids = ids[0];
            }
            else {
                local.ids = ids;
            }
            return instance;
        },
        withRecord(record) {
            const id = local.record && local.record._id;
            local.record = record;
            return instance.withId(id);
        },
        withRecords(...records) {
            if (Array.isArray(records[0])) {
                local.records = records[0];
            }
            else {
                local.records = records;
            }
            return instance;
        },
        select(selection) {
            local.selection = selection;
            return instance;
        },
        then(callback) {
            return promise().then(callback);
        },
        catch(callback) {
            return promise().catch(callback);
        },
        finally(callback) {
            return promise().finally(callback);
        },
    };
    return instance;
}
export function Accessor(model, client) {
    const name = extractModelName(model);
    const instance = {
        query: {
            get read() {
                return AccessorQueryBuilder('read', name, model, client || Client);
            },
            get find() {
                return instance.query.read;
            },
            get get() {
                return instance.query.read;
            },
            get readMany() {
                return AccessorQueryBuilder('readMany', name, model, client || Client);
            },
            get list() {
                return instance.query.readMany;
            },
            get count() {
                return AccessorQueryBuilder('count', name, model, client || Client);
            },
        },
        mutate: {
            get create() {
                return AccessorMutationBuilder('create', name, client || Client);
            },
            get createMany() {
                return AccessorMutationBuilder('createMany', name, client || Client);
            },
            get update() {
                return AccessorMutationBuilder('update', name, client || Client);
            },
            get updateMany() {
                return AccessorMutationBuilder('updateMany', name, client || Client);
            },
            get delete() {
                return AccessorMutationBuilder('delete', name, client || Client);
            },
            get deleteMany() {
                return AccessorMutationBuilder('deleteMany', name, client || Client);
            },
        },
    };
    return instance;
}
